import { Children, type ReactNode } from 'react';
import { useSmallScreen } from '../../lib/util/smallScreen';

/**
 * Port of the DS `DashboardRow` — the handoff's most emphasised rule:
 * CARDS NEVER CARRY THEIR OWN HEIGHT; THE ROW DOES. Two legal modes:
 *
 *   fixed  `height` = 'short' (320) | 'default' (400) | 'tall' (520):
 *          every card stretches to the row and scrolls its own body — pair
 *          with <Card scroll>. A card gaining a tenth item never makes the
 *          row taller than the chart beside it.
 *   auto   `height="auto"`: the row is sized by its TALLEST child and the
 *          others stretch to match, so panels bottom-align with no inner
 *          scrolling (Community Activation's locale table + bars).
 *
 * Never hand-tune a fixed height to fake bottom alignment — use `auto`.
 *
 * On a phone (smallScreen.ts) the cards stack one per line, each keeping the
 * row's height (or its own, for `auto`).
 */
export type RowHeight = 'short' | 'default' | 'tall' | 'auto';

const HEIGHT: Record<Exclude<RowHeight, 'auto'>, string> = {
  short: 'var(--admin-row-short)',
  default: 'var(--admin-row)',
  tall: 'var(--admin-row-tall)',
};

export function DashboardRow({
  cols = 'minmax(0,1fr) minmax(0,1fr)',
  height = 'default',
  children,
}: {
  /** CSS grid-template-columns. */
  cols?: string;
  height?: RowHeight;
  children: ReactNode;
}) {
  const small = useSmallScreen();
  if (small) {
    const each = height === 'auto' ? undefined : HEIGHT[height];
    return (
      <div className="mb-3 flex flex-col gap-3" data-row-height={height}>
        {Children.map(children, (child) => (
          <div className="flex min-h-0 min-w-0 [&>*]:w-full" style={{ height: each }}>
            {child}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div
      className="mb-3.5 grid items-stretch gap-3.5"
      data-row-height={height}
      style={{ gridTemplateColumns: cols, height: height === 'auto' ? 'auto' : HEIGHT[height] }}
    >
      {Children.map(children, (child) => (
        <div className="flex min-h-0 min-w-0 [&>*]:w-full">{child}</div>
      ))}
    </div>
  );
}

/** Kit `FilterBar` — the row of chips/selects/actions above a table. */
export function FilterBar({ children }: { children: ReactNode }) {
  return <div className="mb-4 flex flex-wrap items-center gap-2">{children}</div>;
}

/** Kit `Divider` — 1px vertical rule inside a FilterBar. */
export function FilterDivider() {
  return <div aria-hidden="true" className="mx-1 h-6 w-px bg-[rgba(143,68,32,.14)]" />;
}
